import {
    Button,
    PhoneField,
    DateField,
    ModalOverlay,
    ModalDialog,
    Grid,
    GridItem,
    validatePhone
} from '@flos/react-ui';
import { useRef, useState } from 'react';
import { convertDateToLocalDateDTO } from '../../utils/dateUtil';
import { useCreateActivityHook } from '../../hooks/useCreateActivity';
import type { OnlineActivityDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/activity/';
import { useHippoText } from '../../hooks/useHippoText';
import track, { errorTypes } from 'c9-tracking';
import { useRecoilValue } from 'recoil';
import { accountHolderAtom } from '../../recoil/atoms/accountHolderAtom';

export interface CallMeModalProps {
    activityCode: string;
    showModal: boolean;
    closeModal: () => void;
    closeConfirmationModal?: () => void;
    title?: string;
    description?: string;
    subdescription?: string;
    setOpenErrorModal?: React.Dispatch<React.SetStateAction<boolean>>;
    isDefault?: boolean;
    overlapseModalText?: { modalChooseTimeSubHeader?: string; modalContactedPrompt?: string };
}

const CallMeModal = ({
    activityCode,
    showModal,
    closeModal,
    closeConfirmationModal,
    title,
    description,
    subdescription,
    setOpenErrorModal,
    isDefault = true,
    overlapseModalText
}: CallMeModalProps): React.JSX.Element => {
    const { phoneNumber: savedPhoneNumber } = useRecoilValue(accountHolderAtom);
    const [isValidPhoneNumber, setIsValidPhoneNumber] = useState(validatePhone(savedPhoneNumber));
    const [phoneNumber, setPhoneNumber] = useState<string | undefined>(savedPhoneNumber);
    const [targetDate, setTargetDate] = useState<Date | undefined>(undefined);
    const [requestConfirmed, setRequestConfirmed] = useState(false);
    const { createActivity } = useCreateActivityHook();
    const getHippoText = useHippoText();
    const hasCalledTrackingRef = useRef(false);
    const errorType =
        activityCode === 'dss_unexpected_error_phone_req' ? errorTypes.tech : errorTypes.rule;

    if (showModal) {
        if (!hasCalledTrackingRef.current) {
            void track.fault(
                `Customer encountered a call me modal, errorcode: ${activityCode}`,
                errorType
            );
            hasCalledTrackingRef.current = true;
        }
    }

    const onNextClick = (): void => {
        if (phoneNumber && targetDate) {
            void track.fault(
                `Customer dispatched a request for a call, errorcode: ${activityCode}`,
                errorType
            );
            const onlineActivity: OnlineActivityDTO = {
                activityCode,
                phoneNumber,
                targetDate: convertDateToLocalDateDTO(targetDate)
            };
            void createActivity(onlineActivity).then(() => {
                setRequestConfirmed(true);
            });
        }
    };
    const modalHeaderHippoKey = isDefault
        ? 'DSS-CALL-ME-MODAL-GENERAL-HEADER'
        : 'DSS-EFFECTIVE-DATE-CALL-ME-MODAL-MISS-HEADER';
    const modalSubHeaderHippoKey = isDefault
        ? 'DSS-CALL-ME-MODAL-GENERAL-SUBHEADER'
        : 'DSS-EFFECTIVE-DATE-CALL-ME-MODAL-MISS-SUBHEADER';
    const modalContactedPrompt =
        isDefault && !overlapseModalText?.modalContactedPrompt
            ? 'DSS-CALL-ME-MODAL-GENERAL-NUMBER'
            : overlapseModalText?.modalContactedPrompt
              ? overlapseModalText.modalContactedPrompt
              : 'DSS-EFFECTIVE-DATE-CALL-ME-MODAL-CONTACTED-PROMPT';
    const modalChooseTimeSubHeader =
        overlapseModalText?.modalChooseTimeSubHeader ??
        'DSS-EFFECTIVE-DATE-CALL-ME-MODAL-CHOOSE-TIME-SUBHEADER';

    const hippoText = {
        phoneNumber: getHippoText('DSS-CALL-ME-MODAL-PHONE-NUMBER'),
        chooseDate: getHippoText('DSS-CALL-ME-MODAL-CHOOSE-DATE'),
        bookCallButton: getHippoText('DSS-CALL-ME-MODAL-BOOK-CALL-BUTTON'),
        cancelButton: getHippoText('DSS-CALL-ME-MODAL-CANCEL-BUTTON'),
        confirmationHeader: getHippoText('DSS-CALL-ME-MODAL-CONFIRMATION-HEADER'),
        confirmationSubheaderText: getHippoText('DSS-CALL-ME-MODAL-CONFIRMATION-SUBHEADER-TEXT'),
        confirmationSubheaderDateAndTime: getHippoText(
            'DSS-CALL-ME-MODAL-CONFIRMATION-SUBHEADER-DATE-AND-TIME'
        ),
        commonClose: getHippoText('DSS-COMMON-CLOSE'),
        modalChooseTimeSubHeader: getHippoText(modalChooseTimeSubHeader),
        modalHeader: getHippoText(modalHeaderHippoKey),
        modalSubHeader: getHippoText(modalSubHeaderHippoKey),
        modalContactedPrompt: getHippoText(modalContactedPrompt)
    };

    return (
        <div id="callme-modal" className="u-color-primary-2-dark">
            <ModalOverlay isOpen={showModal}>
                <ModalDialog closeIconHandler={closeModal}>
                    <div className="u-spacing-inset-l">
                        <div className="u-spacing-inset-top-7 u-spacing-inset-bottom-5">
                            <h4 className="u-spacing-stack-m">
                                {!requestConfirmed
                                    ? title ?? hippoText.modalHeader
                                    : hippoText.confirmationHeader}
                            </h4>
                            <p className="u-spacing-stack-m">
                                {!requestConfirmed
                                    ? description ?? hippoText.modalSubHeader
                                    : hippoText.confirmationSubheaderText}
                            </p>
                            {!requestConfirmed &&
                                (!!subdescription ||
                                    (!isDefault && hippoText.modalChooseTimeSubHeader !== '')) && (
                                    <p className="u-spacing-stack-l">
                                        {subdescription ?? hippoText.modalChooseTimeSubHeader}
                                    </p>
                                )}
                            <h6 className="u-spacing-stack-m">
                                {!requestConfirmed
                                    ? hippoText.modalContactedPrompt
                                    : hippoText.confirmationSubheaderDateAndTime}
                            </h6>
                            <Grid>
                                <GridItem span={12}>
                                    <PhoneField
                                        label={hippoText.phoneNumber}
                                        onBlur={(event) => {
                                            setPhoneNumber(event.target.value);
                                        }}
                                        onValidityChange={(isValid: boolean | undefined) => {
                                            setIsValidPhoneNumber(isValid ?? false);
                                        }}
                                        iconShape="phone"
                                        isValid={
                                            savedPhoneNumber
                                                ? validatePhone(phoneNumber)
                                                : undefined
                                        }
                                        value={phoneNumber}
                                        disabled={requestConfirmed}
                                    />
                                </GridItem>
                            </Grid>
                            <Grid className="u-spacing-stack-s">
                                <GridItem span={12}>
                                    <DateField
                                        dateFormat="d. MMMM yyyy"
                                        label={!requestConfirmed ? hippoText.chooseDate : 'Dato'}
                                        minDate={new Date()}
                                        onChange={(date: Date) => {
                                            setTargetDate(date);
                                        }}
                                        selected={targetDate}
                                        disabled={requestConfirmed}
                                    />
                                </GridItem>
                            </Grid>
                            {!requestConfirmed ? (
                                <div>
                                    <Button
                                        disabled={!(isValidPhoneNumber && targetDate)}
                                        onClick={onNextClick}
                                    >
                                        {hippoText.bookCallButton}
                                    </Button>
                                    <Button
                                        theme={'ghost'}
                                        onClick={() => {
                                            setOpenErrorModal
                                                ? setOpenErrorModal(true)
                                                : closeModal();
                                        }}
                                    >
                                        {hippoText.cancelButton}
                                    </Button>
                                </div>
                            ) : (
                                <Button onClick={closeConfirmationModal ?? closeModal}>
                                    {hippoText.commonClose}
                                </Button>
                            )}
                        </div>
                    </div>
                </ModalDialog>
            </ModalOverlay>
        </div>
    );
};

export default CallMeModal;
